import { useEffect, useRef, useState } from "react";
import { ChevronDown, FileText, ListChecks, Plus, Printer, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

function formatarCnpj(valor: string) {
  const digitos = String(valor || "").replace(/\D/g, "");
  if (digitos.length !== 14) return digitos;
  return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

function dataHora(valor: unknown) {
  if (!valor) return "-";
  const data = new Date(String(valor));
  return Number.isNaN(data.getTime()) ? "-" : data.toLocaleString("pt-BR");
}

const ITENS_POR_PAGINA = 50;

function itensDaPagina(itens: any[], pagina: number) {
  const totalPaginas = Math.max(1, Math.ceil(itens.length / ITENS_POR_PAGINA));
  const paginaAtual = Math.min(Math.max(pagina, 1), totalPaginas);
  return itens.slice((paginaAtual - 1) * ITENS_POR_PAGINA, paginaAtual * ITENS_POR_PAGINA);
}

function PaginacaoListagem({ pagina, quantidade, rotulo, onMudarPagina }: { pagina: number; quantidade: number; rotulo: string; onMudarPagina: (pagina: number) => void }) {
  const totalPaginas = Math.max(1, Math.ceil(quantidade / ITENS_POR_PAGINA));
  const paginaAtual = Math.min(Math.max(pagina, 1), totalPaginas);
  if (quantidade <= ITENS_POR_PAGINA) return null;
  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span>Pág. {paginaAtual}/{totalPaginas} ({quantidade} {rotulo})</span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => onMudarPagina(paginaAtual - 1)} disabled={paginaAtual === 1}>&#8249;</Button>
        <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => onMudarPagina(paginaAtual + 1)} disabled={paginaAtual === totalPaginas}>&#8250;</Button>
      </div>
    </div>
  );
}

type ModoExibicaoLotes = "todos" | "hoje" | "nada";

function dataBrasilia(valor: unknown) {
  const data = valor instanceof Date ? valor : new Date(String(valor));
  if (Number.isNaN(data.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(data);
}

function lotesParaExibir(lotes: any[], modo: ModoExibicaoLotes) {
  if (modo === "nada") return [];
  if (modo === "todos") return lotes;
  const hoje = dataBrasilia(new Date());
  return lotes.filter((lote: any) => dataBrasilia(lote.criadoEm) === hoje);
}

function ControleExibicaoLotes({ modo, onMudar }: { modo: ModoExibicaoLotes; onMudar: (modo: ModoExibicaoLotes) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium">Exibir:</span>
      <Button type="button" size="sm" variant={modo === "todos" ? "default" : "outline"} onClick={() => onMudar("todos")}>Tudo</Button>
      <Button type="button" size="sm" variant={modo === "hoje" ? "default" : "outline"} onClick={() => onMudar("hoje")}>Somente hoje</Button>
      <Button type="button" size="sm" variant={modo === "nada" ? "default" : "outline"} onClick={() => onMudar("nada")}>Nada</Button>
    </div>
  );
}

async function baixarPdfListaSeparacao(dados: any) {
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("LISTA DE SEPARAÇÃO", 105, 15, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Lotes: ${dados.loteIds.join(", ")}`, 14, 23);
  const vinculosMeccom = Array.isArray(dados.vinculosMeccom) && dados.vinculosMeccom.length
    ? dados.vinculosMeccom.join(" • ")
    : "-";
  const linhasVinculosMeccom = doc.splitTextToSize(`Vínculos MECOM: ${vinculosMeccom}`, 182);
  doc.text(linhasVinculosMeccom, 14, 28);
  const yGeradaEm = 28 + (linhasVinculosMeccom.length * 4) + 1;
  doc.text(`Gerada em: ${dataHora(dados.geradoEm)}`, 14, yGeradaEm);
  doc.text(`Gerada por: ${dados.geradoPorNome}`, 14, yGeradaEm + 5);
  autoTable(doc, {
    startY: yGeradaEm + 10,
    head: [["SKU", "Descrição", "Quantidade", "Localizações", "Outros Endereços"]],
    body: dados.itens.map((item: any) => [
      item.sku,
      item.descricao || "-",
      Number(item.quantidade).toLocaleString("pt-BR", { maximumFractionDigits: 4 }),
      item.localizacoes.length ? item.localizacoes.join(", ") : "-",
      item.outrosEnderecos.length ? item.outrosEnderecos.join(", ") : "-",
    ]),
    styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
    headStyles: { fillColor: [31, 41, 55] },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 54 },
      2: { cellWidth: 22, halign: "right" },
      3: { cellWidth: 39 },
      4: { cellWidth: 39 },
    },
  });
  doc.save(`lista_separacao_${dados.id}.pdf`);
}

function ListaSeparacaoTab() {
  const utils = trpc.useUtils();
  const [lotesSelecionados, setLotesSelecionados] = useState<number[]>([]);
  const [paginaLotes, setPaginaLotes] = useState(1);
  const [modoExibicaoLotes, setModoExibicaoLotes] = useState<ModoExibicaoLotes>("hoje");
  const { data: lotes = [], isLoading } = trpc.etiquetasExternas.lotes.useQuery();
  const lotesDisponiveis = lotes.filter((lote: any) => !lote.listaSeparacaoId);
  const lotesExibidos = lotesParaExibir(lotes, modoExibicaoLotes);
  const lotesDaPagina = itensDaPagina(lotesExibidos, paginaLotes);
  const todosDisponiveisSelecionados = lotesDisponiveis.length > 0 && lotesDisponiveis.every((lote: any) => lotesSelecionados.includes(Number(lote.id)));

  const gerarLista = trpc.etiquetasExternas.gerarListaSeparacao.useMutation({
    onSuccess: async (dados) => {
      try {
        await baixarPdfListaSeparacao(dados);
        toast.success("Lista de separação gerada.");
      } catch (erro: any) {
        toast.error(erro.message || "A lista foi registrada, mas não foi possível gerar o PDF.");
      }
      setLotesSelecionados([]);
      await utils.etiquetasExternas.lotes.invalidate();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const reimprimirLista = trpc.etiquetasExternas.reimprimirListaSeparacao.useMutation({
    onSuccess: async (dados) => {
      try {
        await baixarPdfListaSeparacao(dados);
      } catch (erro: any) {
        toast.error(erro.message || "Não foi possível gerar o PDF.");
      }
    },
    onError: (erro) => toast.error(erro.message),
  });

  function alternarLote(loteId: number) {
    setLotesSelecionados((atuais) => atuais.includes(loteId) ? atuais.filter((id) => id !== loteId) : [...atuais, loteId]);
  }

  function alternarTodosDisponiveis() {
    setLotesSelecionados(todosDisponiveisSelecionados ? [] : lotesDisponiveis.map((lote: any) => Number(lote.id)));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ListChecks className="h-5 w-5" /> Lista de Separação</CardTitle>
        <CardDescription>Selecione um ou mais lotes para agrupar os SKUs e gerar o PDF.</CardDescription>
        <ControleExibicaoLotes modo={modoExibicaoLotes} onMudar={(modo) => { setModoExibicaoLotes(modo); setPaginaLotes(1); }} />
      </CardHeader>
      {modoExibicaoLotes !== "nada" && <CardContent className="space-y-4">
        <Button
          type="button"
          onClick={() => gerarLista.mutate({ loteIds: lotesSelecionados })}
          disabled={!lotesSelecionados.length || gerarLista.isPending}
        >
          {gerarLista.isPending ? "Gerando..." : `Gerar lista de separação (${lotesSelecionados.length})`}
        </Button>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-2"><input type="checkbox" aria-label="Selecionar todos os lotes disponíveis" checked={todosDisponiveisSelecionados} onChange={alternarTodosDisponiveis} /></th>
                <th className="p-2">Lote</th>
                <th className="p-2">Arquivo</th>
                <th className="p-2">Status da lista</th>
                <th className="p-2">Data de importação</th>
                <th className="p-2">Data de geração</th>
                <th className="p-2">Usuário que gerou</th>
                <th className="p-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="p-4 text-center">Carregando...</td></tr>
              ) : lotesDaPagina.map((lote: any) => {
                const listaGerada = Boolean(lote.listaSeparacaoId);
                return (
                  <tr key={lote.id} className="border-t">
                    <td className="p-2"><input type="checkbox" aria-label={`Selecionar lote ${lote.id}`} checked={lotesSelecionados.includes(Number(lote.id))} disabled={listaGerada} onChange={() => alternarLote(Number(lote.id))} /></td>
                    <td className="p-2">{lote.id}</td>
                    <td className="p-2">{lote.arquivoOriginalNome}</td>
                    <td className="p-2">
                      {listaGerada ? (
                        <>
                          <div>Lista gerada</div>
                          <div className="text-xs text-muted-foreground">{(lote.listaVinculosMeccom || []).join(" • ") || "-"}</div>
                        </>
                      ) : (
                        <>
                          <div>Não gerada</div>
                          <div className="text-xs text-muted-foreground">{String(lote.vinculosMeccom || "").split(" || ").join(" • ") || "-"}</div>
                        </>
                      )}
                    </td>
                    <td className="p-2 whitespace-nowrap">{dataHora(lote.criadoEm)}</td>
                    <td className="p-2 whitespace-nowrap">{dataHora(lote.listaGeradaEm)}</td>
                    <td className="p-2">{lote.listaGeradaPorNome || "-"}</td>
                    <td className="p-2">
                      {listaGerada ? (
                        <Button type="button" size="sm" variant="outline" onClick={() => reimprimirLista.mutate({ listaId: Number(lote.listaSeparacaoId) })} disabled={reimprimirLista.isPending}>
                          <Printer className="mr-2 h-4 w-4" /> Reimprimir
                        </Button>
                      ) : "-"}
                    </td>
                  </tr>
                );
              })}
              {!lotesExibidos.length && !isLoading && <tr><td colSpan={8} className="p-4 text-center text-muted-foreground">Nenhum lote importado no período selecionado.</td></tr>}
            </tbody>
          </table>
        </div>
        <PaginacaoListagem pagina={paginaLotes} quantidade={lotesExibidos.length} rotulo="lotes" onMudarPagina={setPaginaLotes} />
      </CardContent>}
    </Card>
  );
}

export default function ProcessamentoEtiquetasExternas() {
  const utils = trpc.useUtils();
  const inputArquivoRef = useRef<HTMLInputElement>(null);
  const [modeloId, setModeloId] = useState("");
  const [modeloImportacaoId, setModeloImportacaoId] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [duplicados, setDuplicados] = useState<{
    token?: string;
    codigos: string[];
    erros: string[];
    quantidadeComErro: number;
    quantidadeRepetida: number;
    quantidadeValida: number;
  } | null>(null);
  const [loteSelecionadoId, setLoteSelecionadoId] = useState<number | null>(null);
  const [cnpjMapeamento, setCnpjMapeamento] = useState("");
  const [marketplaceId, setMarketplaceId] = useState("");
  const [tipoEnvioId, setTipoEnvioId] = useState("");
  const [paginaMapeamentos, setPaginaMapeamentos] = useState(1);
  const [paginaHistorico, setPaginaHistorico] = useState(1);
  const [paginaEtiquetas, setPaginaEtiquetas] = useState(1);
  const [modoExibicaoHistorico, setModoExibicaoHistorico] = useState<ModoExibicaoLotes>("hoje");

  const { data: modelos = [] } = trpc.etiquetasExternas.modelos.useQuery();
  const { data: mapeamentos = [] } = trpc.etiquetasExternas.mapeamentos.list.useQuery();
  const { data: lotes = [], isLoading: carregandoLotes } = trpc.etiquetasExternas.lotes.useQuery(undefined, { refetchOnWindowFocus: "always" });
  const { data: marketplaces = [] } = trpc.mecom.marketplaces.list.useQuery();
  const { data: envios = [] } = trpc.mecom.enviosByMarketplace.useQuery(
    { marketplaceId: Number(marketplaceId) },
    { enabled: Number(marketplaceId) > 0 }
  );
  const { data: etiquetasLote = [], isLoading: carregandoEtiquetas } = trpc.etiquetasExternas.itens.useQuery(
    { loteId: loteSelecionadoId ?? 0 },
    { enabled: loteSelecionadoId !== null }
  );
  const mapeamentosDaPagina = itensDaPagina(mapeamentos, paginaMapeamentos);
  const lotesDoHistoricoExibidos = lotesParaExibir(lotes, modoExibicaoHistorico);
  const lotesDoHistoricoDaPagina = itensDaPagina(lotesDoHistoricoExibidos, paginaHistorico);
  const etiquetasDaPagina = itensDaPagina(etiquetasLote, paginaEtiquetas);

  useEffect(() => {
    if (!modeloId && modelos.length) setModeloId(String(modelos[0].id));
  }, [modeloId, modelos]);

  useEffect(() => {
    if (!modeloImportacaoId && modelos.length) setModeloImportacaoId(String(modelos[0].id));
  }, [modeloImportacaoId, modelos]);

  useEffect(() => {
    setTipoEnvioId("");
  }, [marketplaceId]);

  const criarMapeamento = trpc.etiquetasExternas.mapeamentos.create.useMutation({
    onSuccess: async () => {
      toast.success("De/para salvo.");
      setCnpjMapeamento("");
      setMarketplaceId("");
      setTipoEnvioId("");
      await utils.etiquetasExternas.mapeamentos.list.invalidate();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const entradaLote = trpc.etiquetasExternas.entradaFeitaPorLote.useMutation({
    onSuccess: async (resultado) => {
      toast.success(`${resultado.quantidade} etiqueta(s) marcada(s) como entrada.`);
      await Promise.all([
        utils.etiquetasExternas.lotes.invalidate(),
        utils.etiquetasExternas.itens.invalidate(),
      ]);
    },
    onError: (erro) => toast.error(erro.message),
  });

  async function enviarArquivo() {
    if (!modeloImportacaoId) return toast.error("Selecione o modelo.");
    if (!arquivo) return toast.error("Selecione o arquivo PDF.");
    setEnviando(true);
    try {
      const form = new FormData();
      form.append("modeloId", modeloImportacaoId);
      form.append("file", arquivo);
      const resposta = await fetch("/api/etiquetas-externas/importar", { method: "POST", body: form });
      const dados = await resposta.json();
      if (resposta.status === 409) {
        setDuplicados({
          token: dados.token,
          codigos: dados.codigosRepetidos ?? [],
          erros: dados.erros ?? [],
          quantidadeComErro: Number(dados.quantidadeComErro ?? 0),
          quantidadeRepetida: Number(dados.quantidadeRepetida ?? 0),
          quantidadeValida: Number(dados.quantidadeValida ?? dados.quantidadeNaoRepetida ?? 0),
        });
        return;
      }
      if (!resposta.ok) throw new Error(dados.erro || "Erro ao importar arquivo.");
      toast.success(`${dados.quantidadeImportada} etiqueta(s) importada(s).`);
      setArquivo(null);
      if (inputArquivoRef.current) inputArquivoRef.current.value = "";
      await utils.etiquetasExternas.lotes.invalidate();
    } catch (erro: any) {
      toast.error(erro.message || "Erro ao importar arquivo.");
    } finally {
      setEnviando(false);
    }
  }

  async function confirmarNaoRepetidos() {
    if (!duplicados?.token) return;
    setEnviando(true);
    try {
      const resposta = await fetch("/api/etiquetas-externas/confirmar-nao-repetidos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: duplicados.token }),
      });
      const dados = await resposta.json();
      if (!resposta.ok) throw new Error(dados.erro || "Erro ao salvar etiquetas.");
      toast.success(`${dados.quantidadeImportada} etiqueta(s) válida(s) importada(s).`);
      setDuplicados(null);
      setArquivo(null);
      if (inputArquivoRef.current) inputArquivoRef.current.value = "";
      await utils.etiquetasExternas.lotes.invalidate();
    } catch (erro: any) {
      toast.error(erro.message || "Erro ao salvar etiquetas.");
    } finally {
      setEnviando(false);
    }
  }

  async function cancelarNaoRepetidos() {
    if (duplicados?.token) {
      await fetch("/api/etiquetas-externas/cancelar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: duplicados.token }),
      });
    }
    setDuplicados(null);
    setArquivo(null);
    if (inputArquivoRef.current) inputArquivoRef.current.value = "";
  }

  function salvarMapeamento() {
    const cnpj = cnpjMapeamento.replace(/\D/g, "");
    if (!modeloId || cnpj.length !== 14 || !marketplaceId || !tipoEnvioId) {
      toast.error("Informe modelo, CNPJ, loja MECOM e tipo de envio.");
      return;
    }
    criarMapeamento.mutate({
      modeloId: Number(modeloId),
      cnpjEmitente: cnpj,
      marketplaceId: Number(marketplaceId),
      tipoEnvioId: Number(tipoEnvioId),
    });
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold">Processamento de Etiquetas Externas</h1>
        <p className="text-sm text-muted-foreground">Selecione o modelo e importe pares de etiqueta &gt; DANFE para processar a entrada MECOM por lote.</p>
      </div>

      <Tabs defaultValue="processamento" className="space-y-4">
        <TabsList>
          <TabsTrigger value="processamento">Processamento</TabsTrigger>
          <TabsTrigger value="lista-separacao">Lista de Separação</TabsTrigger>
        </TabsList>
        <TabsContent value="processamento" className="space-y-6">

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><UploadCloud className="h-5 w-5" /> Importar arquivo</CardTitle>
          <CardDescription>O arquivo deve conter sempre uma etiqueta do modelo selecionado seguida do respectivo DANFE.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div className="grid gap-2">
            <Label htmlFor="modelo">Modelo</Label>
            <select id="modelo" value={modeloImportacaoId} onChange={(event) => setModeloImportacaoId(event.target.value)} className="h-10 rounded-md border bg-background px-3 text-sm">
              <option value="">Selecione</option>
              <option value="automatico">Automático</option>
              {modelos.map((modelo: any) => <option key={modelo.id} value={modelo.id}>{modelo.nome}</option>)}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="arquivo">Arquivo PDF</Label>
            <Input id="arquivo" ref={inputArquivoRef} type="file" accept="application/pdf,.pdf" onChange={(event) => setArquivo(event.target.files?.[0] ?? null)} />
          </div>
          <Button type="button" onClick={enviarArquivo} disabled={enviando}>{enviando ? "Processando..." : "Importar"}</Button>
        </CardContent>
      </Card>

      <Collapsible className="group/depara">
      <Card>
        <CardHeader>
          <CollapsibleTrigger type="button" className="flex w-full items-center justify-between gap-4 text-left">
            <div>
              <CardTitle className="flex items-center gap-2"><Plus className="h-5 w-5" /> De/para do modelo para a entrada MECOM</CardTitle>
              <CardDescription className="mt-1">O vínculo usa somente o CNPJ normalizado extraído do DANFE.</CardDescription>
            </div>
            <ChevronDown className="h-5 w-5 shrink-0 transition-transform group-data-[state=open]/depara:rotate-180" />
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4 md:items-end">
            <div className="grid gap-2"><Label>Modelo</Label><select value={modeloId} onChange={(event) => setModeloId(event.target.value)} className="h-10 rounded-md border bg-background px-3 text-sm"><option value="">Selecione</option>{modelos.map((modelo: any) => <option key={modelo.id} value={modelo.id}>{modelo.nome}</option>)}</select></div>
            <div className="grid gap-2"><Label>CNPJ do DANFE</Label><Input value={formatarCnpj(cnpjMapeamento)} onChange={(event) => setCnpjMapeamento(event.target.value.replace(/\D/g, "").slice(0, 14))} inputMode="numeric" /></div>
            <div className="grid gap-2"><Label>Loja MECOM</Label><select value={marketplaceId} onChange={(event) => setMarketplaceId(event.target.value)} className="h-10 rounded-md border bg-background px-3 text-sm"><option value="">Selecione</option>{marketplaces.map((marketplace: any) => <option key={marketplace.id} value={marketplace.id}>{marketplace.nome}</option>)}</select></div>
            <div className="grid gap-2"><Label>Tipo de envio</Label><select value={tipoEnvioId} onChange={(event) => setTipoEnvioId(event.target.value)} disabled={!marketplaceId} className="h-10 rounded-md border bg-background px-3 text-sm disabled:opacity-50"><option value="">Selecione</option>{envios.map((envio: any) => <option key={envio.tipoEnvioId} value={envio.tipoEnvioId}>{envio.envioNome}</option>)}</select></div>
          </div>
          <Button type="button" variant="outline" onClick={salvarMapeamento} disabled={criarMapeamento.isPending}>{criarMapeamento.isPending ? "Salvando..." : "Salvar de/para"}</Button>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm"><thead className="bg-muted/50 text-left"><tr><th className="p-2">Modelo</th><th className="p-2">CNPJ</th><th className="p-2">Emitente</th><th className="p-2">Loja MECOM</th><th className="p-2">Envio</th><th className="p-2">Transportadora</th></tr></thead><tbody>{mapeamentosDaPagina.map((item: any) => <tr key={item.id} className="border-t"><td className="p-2">{item.modeloNome}</td><td className="p-2">{formatarCnpj(item.cnpjEmitente)}</td><td className="p-2">{item.emitenteNome}</td><td className="p-2">{item.marketplaceNome}</td><td className="p-2">{item.envioNome}</td><td className="p-2">{item.transporteNome}</td></tr>)}{!mapeamentos.length && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Nenhum de/para cadastrado.</td></tr>}</tbody></table>
          </div>
          <PaginacaoListagem pagina={paginaMapeamentos} quantidade={mapeamentos.length} rotulo="de/para" onMudarPagina={setPaginaMapeamentos} />
        </CardContent>
        </CollapsibleContent>
      </Card>
      </Collapsible>

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Histórico de uploads</CardTitle>
          <CardDescription>Os arquivos individuais ficam vinculados ao código extraído de cada etiqueta.</CardDescription>
          <ControleExibicaoLotes modo={modoExibicaoHistorico} onMudar={(modo) => { setModoExibicaoHistorico(modo); setPaginaHistorico(1); }} />
        </CardHeader>
        {modoExibicaoHistorico !== "nada" && <CardContent className="space-y-4">
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm"><thead className="bg-muted/50 text-left"><tr><th className="p-2">Data</th><th className="p-2">Arquivo</th><th className="p-2">Modelo</th><th className="p-2">Lojas MECOM</th><th className="p-2">Importado por</th><th className="p-2">Pendentes</th><th className="p-2">Entrada feita por lote</th><th className="p-2">Impressão</th><th className="p-2">Ações</th></tr></thead><tbody>{carregandoLotes ? <tr><td colSpan={9} className="p-4 text-center">Carregando...</td></tr> : lotesDoHistoricoDaPagina.map((lote: any) => <tr key={lote.id} className="border-t"><td className="p-2 whitespace-nowrap">{dataHora(lote.criadoEm)}</td><td className="p-2"><a href={lote.arquivoOriginalUrl} target="_blank" rel="noreferrer" className="text-primary underline">{lote.arquivoOriginalNome}</a></td><td className="p-2">{lote.modeloNome}</td><td className="p-2">{[...new Set(String(lote.vinculosMeccom || "").split(" || ").filter(Boolean).map((vinculo) => vinculo.split(" | ")[0]).filter(Boolean))].join(" • ") || "-"}</td><td className="p-2">{lote.criadoPorNome}</td><td className="p-2">{lote.quantidadePendentes}</td><td className="p-2">{lote.quantidadeEntradaFeita}</td><td className="p-2 whitespace-nowrap">{lote.jaImpresso == null ? "-" : Number(lote.jaImpresso) === 1 ? "Já impresso" : "Não impresso"}</td><td className="p-2 whitespace-nowrap"><Button type="button" size="sm" variant="outline" onClick={() => { setPaginaEtiquetas(1); setLoteSelecionadoId(Number(lote.id)); }}>Ver etiquetas</Button><Button type="button" size="sm" variant="outline" className="ml-2" onClick={() => window.open(`/api/etiquetas-externas/lotes/${lote.id}/imprimir`, "_blank", "noopener,noreferrer")}>Imprimir etiquetas</Button>{Number(lote.quantidadePendentes) > 0 && <Button type="button" size="sm" className="ml-2" onClick={() => entradaLote.mutate({ loteId: Number(lote.id) })} disabled={entradaLote.isPending}>{entradaLote.isPending ? "Processando..." : "Entrada feita por lote"}</Button>}</td></tr>)}{!lotesDoHistoricoExibidos.length && !carregandoLotes && <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">Nenhum upload importado no período selecionado.</td></tr>}</tbody></table>
          </div>
          <PaginacaoListagem pagina={paginaHistorico} quantidade={lotesDoHistoricoExibidos.length} rotulo="lotes" onMudarPagina={setPaginaHistorico} />
        </CardContent>}
      </Card>

      <Dialog open={Boolean(duplicados)} onOpenChange={(aberto) => { if (!aberto) cancelarNaoRepetidos(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Resultado da validação</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Foram encontradas {duplicados?.quantidadeComErro ?? 0} etiqueta(s) com erro e {duplicados?.quantidadeRepetida ?? 0} etiqueta(s) repetida(s). {duplicados?.quantidadeValida ? `Deseja importar as ${duplicados.quantidadeValida} etiqueta(s) válida(s) restantes?` : "Não há etiquetas válidas para importar."}</p>
          {Boolean(duplicados?.erros.length) && <div><p className="mb-1 text-sm font-medium">Com erro</p><div className="max-h-48 overflow-y-auto rounded border p-3 text-sm">{duplicados?.erros.map((erro, indice) => <div key={`${indice}-${erro}`}>{erro}</div>)}</div></div>}
          {Boolean(duplicados?.codigos.length) && <div><p className="mb-1 text-sm font-medium">Repetidas</p><div className="max-h-48 overflow-y-auto rounded border p-3 text-sm">{duplicados?.codigos.map((codigo) => <div key={codigo}>{codigo}</div>)}</div></div>}
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={cancelarNaoRepetidos} disabled={enviando}>Cancelar</Button>{Boolean(duplicados?.token) && <Button type="button" onClick={confirmarNaoRepetidos} disabled={enviando}>{enviando ? "Importando..." : "Importar válidas"}</Button>}</div>
        </DialogContent>
      </Dialog>

      <Dialog open={loteSelecionadoId !== null} onOpenChange={(aberto) => { if (!aberto) setLoteSelecionadoId(null); }}>
        <DialogContent className="max-h-[85vh] max-w-6xl overflow-y-auto">
          <DialogHeader><DialogTitle>Etiquetas do lote</DialogTitle></DialogHeader>
          <div className="overflow-x-auto rounded border"><table className="w-full text-sm"><thead className="bg-muted/50 text-left"><tr><th className="p-2">Código</th><th className="p-2">NF-e</th><th className="p-2">CNPJ</th><th className="p-2">Itens</th><th className="p-2">Status</th><th className="p-2">Arquivo</th></tr></thead><tbody>{carregandoEtiquetas ? <tr><td colSpan={6} className="p-4 text-center">Carregando...</td></tr> : etiquetasDaPagina.map((etiqueta: any) => <tr key={etiqueta.id} className="border-t"><td className="p-2">{etiqueta.codigoBip}</td><td className="p-2">{etiqueta.notaFiscalNumero}</td><td className="p-2">{formatarCnpj(etiqueta.cnpjEmitente)}</td><td className="p-2">{(() => { try { return JSON.parse(etiqueta.itensJson).map((item: any) => `${item.sku} - ${item.descricao} - ${item.quantidade}`).join("; "); } catch { return "-"; } })()}</td><td className="p-2">{etiqueta.status === "entrada_feita" ? "Entrada feita por lote" : "Pendente"}</td><td className="p-2"><a href={etiqueta.arquivoUrl} target="_blank" rel="noreferrer" className="text-primary underline">Abrir</a></td></tr>)}{!etiquetasLote.length && !carregandoEtiquetas && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">Nenhuma etiqueta encontrada.</td></tr>}</tbody></table></div>
          <PaginacaoListagem pagina={paginaEtiquetas} quantidade={etiquetasLote.length} rotulo="etiquetas" onMudarPagina={setPaginaEtiquetas} />
        </DialogContent>
      </Dialog>
        </TabsContent>
        <TabsContent value="lista-separacao">
          <ListaSeparacaoTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
